import { useState, useEffect } from 'react';

export default function FeatureTour({ isOpen, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (isOpen) {
      // Disable scrolling when tour is active
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const steps = [
    {
      title: 'Welcome to QuickMeet! 👋',
      description: 'Let me show you around and explain how to use all the features. This tour will only take a minute!',
      target: null, // No specific target, centered modal
      position: 'center'
    },
    {
      title: 'Start a Video Meeting',
      description: 'Click "New Meeting" to create an instant video call. You\'ll get a unique meeting ID that you can share with participants. Perfect for quick video calls!',
      target: '[data-tour="new-meeting"]',
      position: 'bottom'
    },
    {
      title: 'Join an Existing Meeting',
      description: 'Have a meeting ID? Enter it here to join an ongoing meeting. You can also paste a full meeting link!',
      target: '[data-tour="join-meeting"]',
      position: 'bottom'
    },
    {
      title: 'Create a Chat Room',
      description: 'Want text-only communication? Create a secure P2P chat room. Great for quick discussions without video!',
      target: '[data-tour="new-chat"]',
      position: 'bottom'
    },
    {
      title: 'Meeting History',
      description: 'View all your past meetings here. See who participated, when it happened, and access any recordings or notes from that session.',
      target: '[data-tour="history-tab"]',
      position: 'right'
    },
    {
      title: 'Recordings',
      description: 'Access all your saved meeting recordings. Download them, preview them, or delete old recordings to free up space. Free tier gets 1 recording, Pro gets 10!',
      target: '[data-tour="recordings-tab"]',
      position: 'right'
    },
    {
      title: 'Meeting Notes',
      description: 'View and manage notes from your meetings. Notes can be private (only you see them) or shared with all participants. Great for action items and key takeaways!',
      target: '[data-tour="notes-tab"]',
      position: 'right'
    },
    {
      title: 'Settings',
      description: 'Customize your QuickMeet experience! Change your display name, adjust video/audio settings, and manage your subscription.',
      target: '[data-tour="settings-button"]',
      position: 'bottom'
    },
    {
      title: 'Premium Features',
      description: 'Want more? Upgrade to Pro for advanced recording, AI transcription, custom themes, and much more. Or go Team for full organization features!',
      target: '[data-tour="upgrade-button"]',
      position: 'bottom'
    },
    {
      title: 'You\'re All Set! 🎉',
      description: 'That\'s everything! You can restart this tour anytime from the help menu. Ready to start your first meeting?',
      target: null,
      position: 'center'
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('quickmeet_feature_tour_completed', 'true');
    setCurrentStep(0);
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('quickmeet_feature_tour_completed', 'true');
    setCurrentStep(0);
    onClose();
  };

  if (!isOpen) return null;

  const step = steps[currentStep];
  const targetElement = step.target ? document.querySelector(step.target) : null;
  const targetRect = targetElement?.getBoundingClientRect();

  // Calculate tooltip position
  let tooltipStyle = {};
  let arrowClass = '';

  if (step.position === 'center' || !targetRect) {
    tooltipStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  } else if (step.position === 'bottom') {
    tooltipStyle = {
      position: 'fixed',
      top: `${targetRect.bottom + 20}px`,
      left: `${targetRect.left + targetRect.width / 2}px`,
      transform: 'translateX(-50%)',
    };
    arrowClass = 'top-arrow';
  } else if (step.position === 'right') {
    tooltipStyle = {
      position: 'fixed',
      top: `${targetRect.top + targetRect.height / 2}px`,
      left: `${targetRect.right + 20}px`,
      transform: 'translateY(-50%)',
    };
    arrowClass = 'left-arrow';
  }

  return (
    <>
      {/* Backdrop overlay */}
      <div className="fixed inset-0 z-[200] pointer-events-none">
        {/* Dark overlay with hole for highlighted element */}
        <svg className="absolute inset-0 w-full h-full pointer-events-auto" style={{ zIndex: 200 }}>
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - 8}
                  y={targetRect.top - 8}
                  width={targetRect.width + 16}
                  height={targetRect.height + 16}
                  rx="12"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.7)"
            mask="url(#spotlight-mask)"
          />
        </svg>

        {/* Highlighted element pulse ring */}
        {targetRect && (
          <div
            className="absolute rounded-xl border-4 border-[#06c] animate-pulse pointer-events-none"
            style={{
              left: `${targetRect.left - 8}px`,
              top: `${targetRect.top - 8}px`,
              width: `${targetRect.width + 16}px`,
              height: `${targetRect.height + 16}px`,
              boxShadow: '0 0 0 4px rgba(0, 102, 204, 0.2), 0 0 20px rgba(0, 102, 204, 0.4)',
            }}
          />
        )}
      </div>

      {/* Tooltip */}
      <div
        className="fixed z-[201] pointer-events-auto"
        style={tooltipStyle}
      >
        <div className="bg-white dark:bg-[#1c1c1e] rounded-2xl p-6 max-w-md w-[90vw] sm:w-96 shadow-2xl border border-[#d2d2d7] dark:border-white/10 transition-colors relative">
          {/* Arrow */}
          {arrowClass === 'top-arrow' && (
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-[#1c1c1e] border-l border-t border-[#d2d2d7] dark:border-white/10 rotate-45" />
          )}
          {arrowClass === 'left-arrow' && (
            <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-white dark:bg-[#1c1c1e] border-l border-b border-[#d2d2d7] dark:border-white/10 rotate-45" />
          )}

          {/* Progress bar */}
          <div className="flex gap-1.5 mb-4">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-1 flex-1 rounded-full transition-all ${
                  index <= currentStep
                    ? 'bg-gradient-to-r from-[#06c] to-[#0055d4]'
                    : 'bg-[#d2d2d7] dark:bg-[#2c2c2e]'
                }`}
              />
            ))}
          </div>

          {/* Content */}
          <h3 className="text-xl font-semibold text-[#1d1d1f] dark:text-white mb-2 transition-colors">
            {step.title}
          </h3>
          <p className="text-[#6e6e73] dark:text-[#8e8e93] leading-relaxed mb-6 transition-colors">
            {step.description}
          </p>

          {/* Actions */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={handleSkip}
              className="px-4 py-2 text-sm text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white transition-colors"
            >
              Skip Tour
            </button>

            <div className="flex gap-2">
              {currentStep > 0 && (
                <button
                  onClick={handlePrev}
                  className="px-4 py-2 rounded-full font-medium text-[#06c] hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] transition-all"
                >
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all"
              >
                {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
              </button>
            </div>
          </div>

          {/* Step counter */}
          <p className="text-center text-xs text-[#86868b] dark:text-[#8e8e93] mt-3 transition-colors">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>
      </div>
    </>
  );
}
